import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useGetProgramsQuery,
    useCreateProgramMutation,
    useUpdateProgramMutation,
    useDeleteProgramMutation,
    useGetProgramSetsQuery,
    useCreateProgramSetMutation,
    useUpdateProgramSetMutation,
    useDeleteProgramSetMutation,
} from "../api";
import type { Program, ProgramSet } from "../api/programs";

const ProgramsPage: React.FC = () => {
    const { t } = useTranslation(["programs", "common"]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState<Program | null>(null);
    const [form, setForm] = useState<Omit<Program, "id">>({
        name: "",
        description: "",
        price_enter: 0,
        price_month: 0,
        price_year: 0,
        type: "",
        safe_dividend_percent: 0,
        safe_payment_interval: 0,
    });
    const [error, setError] = useState<string | null>(null);

    // Состояние для наборов программ
    const [programSetModalVisible, setProgramSetModalVisible] = useState(false);
    const [editingProgramSet, setEditingProgramSet] =
        useState<ProgramSet | null>(null);
    const [programSetForm, setProgramSetForm] = useState<
        Omit<ProgramSet, "id" | "programs"> & { programs: number[] }
    >({
        name: "",
        description: "",
        price_enter: 0,
        programs: [],
    });
    const [programSetError, setProgramSetError] = useState<string | null>(null);

    const {
        data: programs,
        isLoading,
        isError,
        error: queryError,
        refetch,
    } = useGetProgramsQuery();
    const [createProgram] = useCreateProgramMutation();
    const [updateProgram] = useUpdateProgramMutation();
    const [deleteProgram] = useDeleteProgramMutation();

    // Хуки для наборов программ
    const {
        data: programSets,
        isLoading: programSetsLoading,
        isError: programSetsError,
        error: programSetsQueryError,
        refetch: refetchProgramSets,
    } = useGetProgramSetsQuery();
    const [createProgramSet] = useCreateProgramSetMutation();
    const [updateProgramSet] = useUpdateProgramSetMutation();
    const [deleteProgramSet] = useDeleteProgramSetMutation();

    const openModal = (program?: Program) => {
        setEditing(program || null);
        setModalVisible(true);
        if (program) {
            setForm({
                name: program.name,
                description: program.description,
                price_enter: program.price_enter,
                price_month: program.price_month,
                price_year: program.price_year,
                type: program.type,
                safe_dividend_percent: program.safe_dividend_percent,
                safe_payment_interval: program.safe_payment_interval,
            });
        } else {
            setForm({
                name: "",
                description: "",
                price_enter: 0,
                price_month: 0,
                price_year: 0,
                type: "",
                safe_dividend_percent: 0,
                safe_payment_interval: 0,
            });
        }
        setError(null);
    };

    const openProgramSetModal = (programSet?: ProgramSet) => {
        setEditingProgramSet(programSet || null);
        setProgramSetModalVisible(true);
        if (programSet) {
            setProgramSetForm({
                name: programSet.name,
                description: programSet.description,
                price_enter: programSet.price_enter,
                programs: programSet.programs.map((p: any) => p.id),
            });
        } else {
            setProgramSetForm({
                name: "",
                description: "",
                price_enter: 0,
                programs: [],
            });
        }
        setProgramSetError(null);
    };

    const handleOk = async () => {
        if (!form.name || !form.description) {
            setError(t("common:validation_error"));
            return;
        }
        try {
            if (editing) {
                await updateProgram({ id: editing.id, ...form }).unwrap();
            } else {
                await createProgram(form).unwrap();
            }
            setModalVisible(false);
            setEditing(null);
            setForm({
                name: "",
                description: "",
                price_enter: 0,
                price_month: 0,
                price_year: 0,
                type: "",
                safe_dividend_percent: 0,
                safe_payment_interval: 0,
            });
            setError(null);
            refetch();
        } catch (e: any) {
            setError(e?.data?.message || t("common:error"));
        }
    };

    const handleProgramSetOk = async () => {
        if (
            !programSetForm.name ||
            !programSetForm.description ||
            programSetForm.programs.length === 0
        ) {
            setProgramSetError(t("common:validation_error"));
            return;
        }
        try {
            if (editingProgramSet) {
                await updateProgramSet({
                    id: editingProgramSet.id,
                    ...programSetForm,
                }).unwrap();
            } else {
                await createProgramSet(programSetForm).unwrap();
            }
            setProgramSetModalVisible(false);
            setEditingProgramSet(null);
            setProgramSetForm({
                name: "",
                description: "",
                price_enter: 0,
                programs: [],
            });
            setProgramSetError(null);
            refetchProgramSets();
        } catch (e: any) {
            setProgramSetError(e?.data?.message || t("common:error"));
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm(t("common:delete") + "?")) {
            try {
                await deleteProgram(id).unwrap();
                refetch();
            } catch (e: any) {
                setError(e?.data?.message || t("common:error"));
            }
        }
    };

    const handleDeleteProgramSet = async (id: number) => {
        if (window.confirm(t("common:delete") + "?")) {
            try {
                await deleteProgramSet(id).unwrap();
                refetchProgramSets();
            } catch (e: any) {
                setProgramSetError(e?.data?.message || t("common:error"));
            }
        }
    };

    const toggleProgramSelection = (programId: number) => {
        setProgramSetForm((prev) => ({
            ...prev,
            programs: prev.programs.includes(programId)
                ? prev.programs.filter((id) => id !== programId)
                : [...prev.programs, programId],
        }));
    };

    const getTypeTranslation = (type: string) => {
        switch (type) {
            case "structural":
                return t("programs:type_structural");
            case "charity":
                return t("programs:type_charity");
            case "estate":
                return t("programs:type_estate");
            case "star":
                return t("programs:type_star");
            case "custom":
                return t("programs:type_custom");
            default:
                return type;
        }
    };

    return (
        <div className="mx-auto p-4">
            <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <span className="inline-block text-blue-600">📚</span>
                {t("programs:title", "Программы")}
            </h1>

            {/* Секция программ */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Программы</h2>
                    <button
                        onClick={() => openModal()}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                    >
                        {t("programs:add")}
                    </button>
                </div>

                {isLoading ? (
                    <div className="text-gray-500 dark:text-gray-400 py-8 text-center">
                        {t("common:loading")}
                    </div>
                ) : isError ? (
                    <div className="text-red-600 py-8 text-center">
                        {t("common:error")}:{" "}
                        {String(
                            (queryError as any)?.data?.message || queryError
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg shadow">
                        <table className="min-w-full bg-white border border-gray-200 dark:border-gray-700">
                            <thead className="bg-gray-100 dark:bg-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:name")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:description")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:price_enter")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:price_month")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:price_year")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:type")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:safe_dividend_percent")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:safe_payment_interval")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:actions")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {programs?.map((program: any) => (
                                    <tr
                                        key={program.id}
                                        className="hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-900 dark:text-white transition"
                                    >
                                        <td className="px-4 py-3 border-b">
                                            {program.name}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.description}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.price_enter}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.price_month}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.price_year}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {getTypeTranslation(program.type)}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.safe_dividend_percent}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {program.safe_payment_interval}
                                        </td>
                                        <td className="px-4 py-3 border-b text-center">
                                            <button
                                                onClick={() =>
                                                    openModal(program)
                                                }
                                                className="inline-block px-3 py-1 bg-yellow-400 text-gray-900 dark:text-white rounded hover:bg-yellow-500 transition mr-2"
                                            >
                                                {t("common:edit")}
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDelete(program.id)
                                                }
                                                className="inline-block px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                            >
                                                {t("common:delete")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {programs?.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={10}
                                            className="text-center text-gray-400 py-8"
                                        >
                                            {t("common:no_data", "Нет данных")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Секция наборов программ */}
            <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">
                        {t("programs:program_sets")}
                    </h2>
                    <button
                        onClick={() => openProgramSetModal()}
                        className="px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
                    >
                        {t("programs:add_program_set")}
                    </button>
                </div>

                {programSetsLoading ? (
                    <div className="text-gray-500 dark:text-gray-400 py-8 text-center">
                        {t("common:loading")}
                    </div>
                ) : programSetsError ? (
                    <div className="text-red-600 py-8 text-center">
                        {t("common:error")}:{" "}
                        {String(
                            (programSetsQueryError as any)?.data?.message ||
                                programSetsQueryError
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg shadow">
                        <table className="min-w-full bg-white border border-gray-200 dark:border-gray-700">
                            <thead className="bg-gray-100 dark:bg-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:name")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:description")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:price_enter")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:program_set_programs")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b">
                                        {t("programs:actions")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {programSets?.map((programSet: any) => (
                                    <tr
                                        key={programSet.id}
                                        className="hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-900 dark:text-white transition"
                                    >
                                        <td className="px-4 py-3 border-b">
                                            {programSet.name}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {programSet.description}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            {programSet.price_enter}
                                        </td>
                                        <td className="px-4 py-3 border-b">
                                            <div className="flex flex-wrap gap-1">
                                                {programSet.programs.map(
                                                    (program: any) => (
                                                        <span
                                                            key={program.id}
                                                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                                                        >
                                                            {program.name}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 border-b text-center">
                                            <button
                                                onClick={() =>
                                                    openProgramSetModal(
                                                        programSet
                                                    )
                                                }
                                                className="inline-block px-3 py-1 bg-yellow-400 text-gray-900 dark:text-white rounded hover:bg-yellow-500 transition mr-2"
                                            >
                                                {t("common:edit")}
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleDeleteProgramSet(
                                                        programSet.id
                                                    )
                                                }
                                                className="inline-block px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                            >
                                                {t("common:delete")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {programSets?.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="text-center text-gray-400 py-8"
                                        >
                                            {t("common:no_data", "Нет данных")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Модальное окно для программ */}
            {modalVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 w-full max-w-md relative animate-fade-in">
                        <button
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:text-gray-300 text-2xl font-bold"
                            onClick={() => {
                                setModalVisible(false);
                                setEditing(null);
                                setError(null);
                            }}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white">
                            {editing
                                ? t(
                                      "programs:edit_program",
                                      "Редактировать программу"
                                  )
                                : t(
                                      "programs:add_program",
                                      "Добавить программу"
                                  )}
                        </h2>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleOk();
                            }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:name")}
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            name: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:description")}
                                </label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            description: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:type")}
                                </label>
                                <input
                                    type="text"
                                    value={form.type}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            type: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("programs:price_enter")}
                                    </label>
                                    <input
                                        type="number"
                                        value={form.price_enter}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                price_enter: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("programs:price_month")}
                                    </label>
                                    <input
                                        type="number"
                                        value={form.price_month}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                price_month: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("programs:price_year")}
                                    </label>
                                    <input
                                        type="number"
                                        value={form.price_year}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                price_year: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("programs:safe_dividend_percent")}
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={form.safe_dividend_percent}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                safe_dividend_percent: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("programs:safe_payment_interval")}
                                    </label>
                                    <input
                                        type="number"
                                        value={form.safe_payment_interval}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                safe_payment_interval: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        required
                                    />
                                </div>
                            </div>
                            {error && (
                                <div className="text-red-600 text-sm mt-2">
                                    {error}
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="px-5 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                                >
                                    {t("common:save")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setModalVisible(false);
                                        setEditing(null);
                                        setError(null);
                                    }}
                                    className="px-5 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 transition"
                                >
                                    {t("common:cancel")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Модальное окно для наборов программ */}
            {programSetModalVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 w-full max-w-lg relative animate-fade-in">
                        <button
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:text-gray-300 text-2xl font-bold"
                            onClick={() => {
                                setProgramSetModalVisible(false);
                                setEditingProgramSet(null);
                                setProgramSetError(null);
                            }}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white">
                            {editingProgramSet
                                ? t(
                                      "programs:edit_program_set",
                                      "Редактировать набор"
                                  )
                                : t(
                                      "programs:add_program_set",
                                      "Добавить набор"
                                  )}
                        </h2>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleProgramSetOk();
                            }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:program_set_name")}
                                </label>
                                <input
                                    type="text"
                                    value={programSetForm.name}
                                    onChange={(e) =>
                                        setProgramSetForm((f) => ({
                                            ...f,
                                            name: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:program_set_description")}
                                </label>
                                <input
                                    type="text"
                                    value={programSetForm.description}
                                    onChange={(e) =>
                                        setProgramSetForm((f) => ({
                                            ...f,
                                            description: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:program_set_price_enter")}
                                </label>
                                <input
                                    type="number"
                                    value={programSetForm.price_enter}
                                    onChange={(e) =>
                                        setProgramSetForm((f) => ({
                                            ...f,
                                            price_enter: Number(e.target.value),
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("programs:program_set_programs")}
                                </label>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded p-2">
                                    {programs?.map((program: any) => (
                                        <label
                                            key={program.id}
                                            className="flex items-center space-x-2 py-1"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={programSetForm.programs.includes(
                                                    program.id
                                                )}
                                                onChange={() =>
                                                    toggleProgramSelection(
                                                        program.id
                                                    )
                                                }
                                                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-900 dark:text-white">
                                                {program.name}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {programSetError && (
                                <div className="text-red-600 text-sm mt-2">
                                    {programSetError}
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
                                >
                                    {t("common:save")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setProgramSetModalVisible(false);
                                        setEditingProgramSet(null);
                                        setProgramSetError(null);
                                    }}
                                    className="px-5 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 transition"
                                >
                                    {t("common:cancel")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramsPage;
